import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Delete,
  Sync,
  Refresh,
  Visibility,
  Upgrade,
  ContentCopy,
  VpnKey
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/api';
import { Tenant, TenantUpdate, TenantDeployment, Size } from '../types';
import { Customer } from '../types/customer';
import StatusChip from '../components/StatusChip';
import LoadingButton from '../components/LoadingButton';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tenant-tabpanel-${index}`}
      aria-labelledby={`tenant-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const TenantDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [deployments, setDeployments] = useState<TenantDeployment[]>([]);
  const [size, setSize] = useState<Size | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);

  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editData, setEditData] = useState<TenantUpdate>({});

  // Logs dialog
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [selectedDeploymentLogs, setSelectedDeploymentLogs] = useState<string>('');

  // Employee portal invite
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [inviteLoading, setInviteLoading] = useState(false);

  // Default medium size parameters
  const getDefaultMediumParameters = () => [
    { parameter_name: 'max_users', parameter_value: 15, description: 'Default medium size limit' },
    { parameter_name: 'max_projects', parameter_value: 10, description: 'Default medium size limit' },
    { parameter_name: 'max_connections_per_project', parameter_value: 2, description: 'Default medium size limit' },
    { parameter_name: 'max_tables_per_connection', parameter_value: 20, description: 'Default medium size limit' },
    { parameter_name: 'max_records_per_table', parameter_value: 2000000, description: 'Default medium size limit' },
    { parameter_name: 'max_files_per_project', parameter_value: 20, description: 'Default medium size limit' }
  ];

  const loadTenantData = async () => {
    if (!id) return;

    try {
      setError('');
      const [tenantData, deploymentsData] = await Promise.all([
        apiService.getTenant(parseInt(id)),
        apiService.getTenantDeployments(parseInt(id))
      ]);
      
      setTenant(tenantData);
      setDeployments(deploymentsData.deployments);

      // Load customer data if tenant has customer_id
      if (tenantData.customer_id) {
        try {
          const customerData = await apiService.getCustomer(tenantData.customer_id);
          setCustomer(customerData);
        } catch (customerErr) {
          console.error('Failed to load customer data:', customerErr);
        }
      }

      // Load size data if tenant has size_id
      if (tenantData.size_id) {
        try {
          const sizeResponse = await apiService.getSize(tenantData.size_id);
          setSize(sizeResponse.size);
        } catch (sizeErr) {
          console.error('Failed to load size data:', sizeErr);
        }
      }
    } catch (err: any) {
      console.error('Failed to load tenant data:', err);
      setError(err.detail || 'Failed to load tenant data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditTenant = async () => {
    if (!tenant) return;

    try {
      setEditLoading(true);
      const updatedTenant = await apiService.updateTenant(tenant.id, editData);
      setTenant(updatedTenant);
      setEditDialogOpen(false);
      toast.success(t('tenant.tenantUpdated'));
    } catch (err: any) {
      console.error('Failed to update tenant:', err);
      toast.error(err.detail || 'Failed to update tenant');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteTenant = async () => {
    if (!tenant) return;
    
    if (!window.confirm(t('tenant.deleteConfirm'))) {
      return;
    }

    try {
      await apiService.deleteTenant(tenant.id);
      toast.success(t('tenant.tenantDeleted'));
      navigate('/tenants');
    } catch (err: any) {
      console.error('Failed to delete tenant:', err);
      toast.error(err.detail || t('messages.errorDeletingTenant'));
    }
  };

  const handleUpgradeTenant = async () => {
    if (!tenant) return;
    
    if (!window.confirm(t('tenant.upgradeConfirm'))) {
      return;
    }

    try {
      const deployment = await apiService.upgradeTenant(tenant.id);
      toast.success(t('tenant.tenantUpgraded'));
      // Navigate to deployment detail page to monitor progress
      navigate(`/deployments/${deployment.id}`);
    } catch (err: any) {
      console.error('Failed to upgrade tenant:', err);
      toast.error(err.detail || 'Failed to start tenant upgrade');
    }
  };

  const handleGenerateInviteCode = async () => {
    if (!tenant) return;

    try {
      setInviteLoading(true);
      const result = await apiService.createEmployeePortalInviteCode(tenant.id);
      setInviteCode(result.invite_code);
      toast.success(t('tenant.inviteCodeGenerated'));
    } catch (err: any) {
      console.error('Failed to generate invite code:', err);
      toast.error(err.detail || 'Failed to generate invite code');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCopyInviteCode = async () => {
    if (!inviteCode) return;
    await navigator.clipboard.writeText(inviteCode);
    toast.success(t('tenant.inviteCodeCopied'));
  };

  const handleSyncTenant = async () => {
    if (!tenant) return;

    try {
      await apiService.syncTenantStatus(tenant.id);
      toast.success('Tenant status synchronized');
      loadTenantData();
    } catch (err: any) {
      console.error('Failed to sync tenant:', err);
      toast.error(err.detail || 'Failed to sync tenant status');
    }
  };

  const handleViewLogs = async (deployment: TenantDeployment) => {
    try {
      // Clear previous logs to avoid showing stale data
      setSelectedDeploymentLogs('Loading logs...');
      setLogsDialogOpen(true);
      
      // Fetch fresh logs every time
      const logsData = await apiService.getDeploymentLogs(deployment.id);
      setSelectedDeploymentLogs(logsData.logs);
    } catch (err: any) {
      console.error('Failed to load logs:', err);
      setSelectedDeploymentLogs('Failed to load logs: ' + (err.detail || err.message || 'Unknown error'));
      toast.error(err.detail || 'Failed to load deployment logs');
    }
  };

  const openEditDialog = () => {
    if (tenant) {
      setEditData({
        contact_email: tenant.contact_email,
        description: tenant.description || ''
      });
      setEditDialogOpen(true);
    }
  };

  useEffect(() => {
    loadTenantData();
  }, [id]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        Loading tenant details...
      </Box>
    );
  }

  if (!tenant) {
    return (
      <Box>
        <Alert severity="error">Tenant not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <IconButton onClick={() => navigate('/tenants')}>
          <ArrowBack />
        </IconButton>
        <Box flex={1}>
          <Typography variant="h4" component="h1">
            {tenant.name}
          </Typography>
          <StatusChip status={tenant.status} />
          {tenant.status === 'active' && (
            <Button
              variant="text"
              size="small"
              onClick={() => window.open(`https://${tenant.name}.usewai.com`, '_blank')}
              sx={{ 
                minWidth: 'auto',
                color: 'primary.main',
                textDecoration: 'underline',
                '&:hover': { backgroundColor: 'transparent', color: 'primary.dark' }
              }}
            >
              🌐 {tenant.name}.usewai.com
            </Button>
          )}
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<Sync />}
            onClick={handleSyncTenant}
            size="small"
          >
            {t('common.sync')}
          </Button>
          {tenant.status === 'active' && (
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={openEditDialog}
              size="small"
            >
              {t('common.edit')}
            </Button>
          )}
          {tenant.status === 'active' && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Upgrade />}
              onClick={handleUpgradeTenant}
              size="small"
            >
              {t('tenant.upgrade')}
            </Button>
          )}
          {tenant.can_be_deleted && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={handleDeleteTenant}
              size="small"
            >
              {t('common.delete')}
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Card>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label={t('tenant.overview')} />
          <Tab label={t('tenant.deployments')} />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('tenant.basicInformation')}
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        {t('tenant.tenantName')}
                      </Typography>
                      <Typography>{tenant.name}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        {t('tenant.contactEmail')}
                      </Typography>
                      <Typography>{tenant.contact_email}</Typography>
                    </Box>
                    {tenant.description && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          {t('tenant.description')}
                        </Typography>
                        <Typography>{tenant.description}</Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {customer && (
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {t('customers.customerDetails')}
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={2}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          {t('customers.fields.name')}
                        </Typography>
                        <Typography>{customer.name}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          {t('customers.fields.countryRegion')}
                        </Typography>
                        <Typography>{customer.country_region}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          {t('customers.fields.businessOwnerEmail')}
                        </Typography>
                        <Typography>{customer.business_owner_email}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          {t('customers.fields.technicalContactEmail')}
                        </Typography>
                        <Typography>{customer.technical_contact_email}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          {t('customers.fields.mainCommunicationChannel')}
                        </Typography>
                        <Chip 
                          label={t(`customers.communicationChannels.${customer.main_communication_channel}`)}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('tenant.statusMetadata')}
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        {t('tenant.status')}
                      </Typography>
                      <StatusChip status={tenant.status} />
                    </Box>
                    {tenant.domain && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          {t('tenant.domain')}
                        </Typography>
                        <Chip label={tenant.domain} variant="outlined" />
                      </Box>
                    )}
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        {t('tenant.created')}
                      </Typography>
                      <Typography>{format(new Date(tenant.created_at), 'PPpp')}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        {t('tenant.lastUpdated')}
                      </Typography>
                      <Typography>{format(new Date(tenant.updated_at), 'PPpp')}</Typography>
                    </Box>
                    {tenant.deleted_at && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          {t('tenant.deleted')}
                        </Typography>
                        <Typography>{format(new Date(tenant.deleted_at), 'PPpp')}</Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Size Configuration Section */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('sizes.sizeConfiguration')}
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        {t('sizes.assignedSize')}
                      </Typography>
                      <Chip 
                        label={size ? size.display_name : t('sizes.defaultMediumSize')}
                        variant="outlined"
                        color={size ? 'primary' : 'default'}
                      />
                    </Box>
                    {size && size.description && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          {t('tenant.description')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {size.description}
                        </Typography>
                      </Box>
                    )}
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        {t('sizes.parameters')}
                      </Typography>
                      {(() => {
                        const parameters = size?.parameters || getDefaultMediumParameters();
                        return parameters.length > 0 ? (
                          <Box sx={{ mt: 1 }}>
                            {parameters.map((param, index) => (
                              <Box key={index} sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                py: 0.5,
                                px: 1,
                                my: 0.5,
                                bgcolor: index % 2 === 0 ? 'action.hover' : 'transparent',
                                borderRadius: 1
                              }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {t(`sizes.defaultParameters.${param.parameter_name}`) || param.parameter_name}
                                </Typography>
                                <Chip 
                                  label={param.parameter_value.toLocaleString()}
                                  size="small"
                                  variant="outlined"
                                  color={size ? 'primary' : 'default'}
                                />
                              </Box>
                            ))}
                            {!size && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                                * {t('sizes.defaultMediumSize')} parameters shown
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            {t('sizes.noActiveSizes')}
                          </Typography>
                        );
                      })()}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {tenant.status === 'active' && (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {t('tenant.employeePortalInvite')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {t('tenant.employeePortalInviteDescription')}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                      <LoadingButton
                        variant="contained"
                        startIcon={<VpnKey />}
                        loading={inviteLoading}
                        onClick={handleGenerateInviteCode}
                      >
                        {t('tenant.generateInviteCode')}
                      </LoadingButton>
                      {inviteCode && (
                        <>
                          <TextField
                            value={inviteCode}
                            size="small"
                            InputProps={{ readOnly: true }}
                            sx={{ minWidth: 280, flexGrow: 1 }}
                          />
                          <IconButton onClick={handleCopyInviteCode} aria-label={t('tenant.copyInviteCode')}>
                            <ContentCopy fontSize="small" />
                          </IconButton>
                          <Typography variant="caption" color="text.secondary">
                            {t('tenant.inviteCodeExpires')}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box display="flex" justifyContent="between" alignItems="center" mb={2}>
            <Typography variant="h6">Deployment History</Typography>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={loadTenantData}
              size="small"
            >
              Refresh
            </Button>
          </Box>

          {deployments.length === 0 ? (
            <Typography color="text.secondary" align="center" py={4}>
              No deployments found
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Operation</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Started</TableCell>
                    <TableCell>Completed</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deployments.map((deployment) => (
                    <TableRow key={deployment.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {deployment.operation_type}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusChip status={deployment.status} />
                      </TableCell>
                      <TableCell>
                        {format(new Date(deployment.started_at), 'MMM dd, HH:mm')}
                      </TableCell>
                      <TableCell>
                        {deployment.completed_at 
                          ? format(new Date(deployment.completed_at), 'MMM dd, HH:mm')
                          : '-'
                        }
                      </TableCell>
                      <TableCell>
                        {deployment.duration_seconds 
                          ? `${Math.round(deployment.duration_seconds / 60)}m`
                          : '-'
                        }
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleViewLogs(deployment)}
                          title="View Logs"
                        >
                          <Visibility />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Tenant</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Contact Email"
            type="email"
            fullWidth
            variant="outlined"
            value={editData.contact_email || ''}
            onChange={(e) => setEditData({ ...editData, contact_email: e.target.value })}
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={editData.description || ''}
            onChange={(e) => setEditData({ ...editData, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <LoadingButton
            onClick={handleEditTenant}
            loading={editLoading}
            variant="contained"
          >
            Update Tenant
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Logs Dialog */}
      <Dialog open={logsDialogOpen} onClose={() => setLogsDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Deployment Logs</DialogTitle>
        <DialogContent>
          <Box
            component="pre"
            sx={{
              backgroundColor: '#f5f5f5',
              padding: 2,
              borderRadius: 1,
              fontSize: '0.875rem',
              fontFamily: 'monospace',
              overflow: 'auto',
              maxHeight: '60vh',
              whiteSpace: 'pre-wrap'
            }}
          >
            {selectedDeploymentLogs || 'No logs available'}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TenantDetailPage;